import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@mui/material';

import {
	getBirthdateDisclaimer,
	getBirthdateInputMax,
	getBirthdateInputMin,
	getDocumentExpirationDisclaimer,
	getDocumentExpirationInputMax,
	getDocumentExpirationInputMin,
	getTravelDateDisclaimer,
	TRAVEL_DATE_INPUT_MAX,
	TRAVEL_DATE_INPUT_MIN,
} from '../../helpers/travelDateValidation';

export type ControlledDatePickerPurpose = 'travel' | 'birthdate' | 'documentExpiration';

const DATE_PURPOSE_CONFIG: Record<
	ControlledDatePickerPurpose,
	{
		inputMin: () => string;
		inputMax: () => string;
		getDisclaimer: (value: string, validateRanges: boolean) => string | null;
	}
> = {
	travel: {
		inputMin: () => TRAVEL_DATE_INPUT_MIN,
		inputMax: () => TRAVEL_DATE_INPUT_MAX,
		getDisclaimer: (value, validateRanges) => getTravelDateDisclaimer(value, { validateRanges }),
	},
	birthdate: {
		inputMin: getBirthdateInputMin,
		inputMax: getBirthdateInputMax,
		getDisclaimer: (value, validateRanges) => getBirthdateDisclaimer(value, { validateRanges }),
	},
	documentExpiration: {
		inputMin: getDocumentExpirationInputMin,
		inputMax: getDocumentExpirationInputMax,
		getDisclaimer: (value, validateRanges) => getDocumentExpirationDisclaimer(value, { validateRanges }),
	},
};

interface Props {
	name: string;
	rules?: Record<string, any>;
	inputFormat?: string;
	size?: 'small' | 'medium';
	variant?: 'outlined' | 'standard';
	required?: boolean;
	/** `travel` (default), `birthdate` o `documentExpiration` (posterior a hoy). */
	datePurpose?: ControlledDatePickerPurpose;
}

type DateFieldProps = {
	field: {
		name: string;
		value: string;
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
		onBlur: () => void;
		ref: React.Ref<HTMLInputElement>;
	};
	hardError?: string;
	required: boolean;
	variant: 'outlined' | 'standard';
	size: 'small' | 'medium';
	datePurpose: ControlledDatePickerPurpose;
};

const clearSoftWarnings = (
	userEditedRef: React.MutableRefObject<boolean>,
	setSoftWarning: React.Dispatch<React.SetStateAction<string | null>>,
): void => {
	userEditedRef.current = false;
	setSoftWarning(null);
};

const DatePickerField = ({ field, hardError, required, variant, size, datePurpose }: DateFieldProps): JSX.Element => {
	const {
		inputMin: resolveInputMin,
		inputMax: resolveInputMax,
		getDisclaimer: resolveDisclaimer,
	} = DATE_PURPOSE_CONFIG[datePurpose];
	const inputMin = resolveInputMin();
	const inputMax = resolveInputMax();

	const getDisclaimer = useCallback(
		(value: string | null | undefined, validateRanges: boolean) => {
			if (!validateRanges) {
				return null;
			}
			return resolveDisclaimer(value ?? '', true);
		},
		[resolveDisclaimer],
	);
	const { formState } = useFormContext();
	const userEditedRef = useRef(false);
	const isUserChangeRef = useRef(false);
	const [softWarning, setSoftWarning] = useState<string | null>(null);

	useEffect(() => {
		clearSoftWarnings(userEditedRef, setSoftWarning);
		isUserChangeRef.current = false;
	}, [field.name]);

	useEffect(() => {
		if (formState.isSubmitSuccessful) {
			clearSoftWarnings(userEditedRef, setSoftWarning);
		}
	}, [formState.isSubmitSuccessful]);

	const refreshSoftWarning = useCallback(
		(value: string | null | undefined, validateRanges: boolean) => {
			setSoftWarning(getDisclaimer(value, validateRanges));
		},
		[getDisclaimer],
	);

	useEffect(() => {
		if (isUserChangeRef.current) {
			isUserChangeRef.current = false;
			if (userEditedRef.current) {
				refreshSoftWarning(field.value, true);
			}
			return;
		}

		clearSoftWarnings(userEditedRef, setSoftWarning);
	}, [field.value, refreshSoftWarning]);

	const helperText = hardError || softWarning || undefined;
	const showAsError = Boolean(hardError || softWarning);

	return (
		<TextField
			required={required}
			type="date"
			variant={variant}
			size={size}
			sx={{ width: '100%', backgroundColor: '#FFFFFF' }}
			error={showAsError}
			helperText={helperText}
			name={field.name}
			onBlur={(event) => {
				field.onBlur();
				if (userEditedRef.current) {
					refreshSoftWarning(event.target.value, true);
				}
			}}
			onChange={(event) => {
				isUserChangeRef.current = true;
				userEditedRef.current = true;
				field.onChange(event);
				refreshSoftWarning(event.target.value, true);
			}}
			ref={field.ref}
			value={field.value || ''}
			inputProps={{
				min: inputMin,
				max: inputMax,
			}}
		/>
	);
};

export const ControlledDatePicker = ({
	name,
	rules,
	variant = 'outlined',
	size = 'small',
	required = true,
	datePurpose = 'travel',
}: Props): JSX.Element => {
	const { control, formState } = useFormContext();
	const hardError = formState.errors[name]?.message as string | undefined;

	return (
		<Controller
			// @ts-ignore
			name={name}
			control={control}
			rules={rules}
			render={({ field }) => (
				<DatePickerField
					field={field}
					hardError={hardError}
					required={required}
					variant={variant}
					size={size}
					datePurpose={datePurpose}
				/>
			)}
		/>
	);
};
